import {
  Box,
  IconButton,
  Skeleton,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';

import {
  useCreateNewThreadWithCommentForValleyBankLoanAccountMutation,
  useGetValleyBankCommentsForLoanAccountQuery,
  ValleyBankLoanAccountThreadGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { colors } from '~/packages/common-frontend/colors';
import {
  DetailedUpdatesDrawer,
  OverviewUpdatesDrawer,
} from '~/packages/common-frontend/components/Comments/UpdatesDrawer';
import { Icon } from '~/packages/common-frontend/components/icons';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { CustomerTag } from '~/valleyBank/domains/Customers/hooks/CustomerContext';
import {
  generateLoanAccountUrl,
  LoanAccountTag,
} from '~/valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';

export const LoanAccountUnderMarginDetailedUpdatesDrawer = ({
  loanAccountInfo,
  isOpen,
  onClose,
}: {
  loanAccountInfo: {
    loanAccountNumber: string;
    cifNum: string;
    name: string;
  };
  isOpen: boolean;
  onClose: () => void;
}) => {
  const toast = useToast();
  const { dashboardMode } = useDataMetadataContext();

  const {
    data: threadsForLoanAccountData,
    refetch: refetchThreads,
  } = useGetValleyBankCommentsForLoanAccountQuery({
    variables: {
      loanAccountNum: loanAccountInfo.loanAccountNumber,
    },
    fetchPolicy: 'network-only',
    skip: dashboardMode !== 'current',
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const [
    createNewThreadWithCommentForValleyBankLoanAccountMutation,
  ] = useCreateNewThreadWithCommentForValleyBankLoanAccountMutation({
    fetchPolicy: 'network-only',
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  if (dashboardMode !== 'current') return null;
  if (!threadsForLoanAccountData) return <Skeleton height="600px" />;

  return (
    <DetailedUpdatesDrawer
      isOpen={isOpen}
      onClose={onClose}
      threads={threadsForLoanAccountData.valleyBankCommentsForLoanAccount}
      refetchThreads={refetchThreads}
      externalOrgOptions={[]}
      onCreateNewCommentOnExistingThread={async (_newCommentId: string) => {
        // @valley if we want to send emails for comments, add it here

        refetchThreads();
      }}
      createNewThreadWithCommentForDomain={async ({
        organizationIds: _unused, // we only grant thread permissions to valley itself so no need to pass org ids
        authorUserId,
        comment,
      }) => {
        await createNewThreadWithCommentForValleyBankLoanAccountMutation({
          variables: {
            authorUserId,
            comment,
            loanAccountNumber: loanAccountInfo.loanAccountNumber,
            isPinned: true,
          },
        });

        refetchThreads();
      }}
    >
      <Box fontSize="xs" mx="16px">
        <LoanAccountTag loanAccountId={loanAccountInfo.loanAccountNumber} />
      </Box>

      <Box fontSize="xs" mx="16px">
        <CustomerTag
          cifnum={loanAccountInfo.cifNum}
          label={`Borrower: ${loanAccountInfo.name} (${loanAccountInfo.cifNum})`}
        />
      </Box>
    </DetailedUpdatesDrawer>
  );
};

export const LoanAccountsUnderMarginOverviewUpdatesDrawer = ({
  threads,
  refetchThreads,
}: {
  threads?: ValleyBankLoanAccountThreadGQLType[];
  refetchThreads: () => void;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const threadsWithMetadata = useMemo(() => {
    if (threads) {
      return threads.map(({ loanAccountNum, thread }) => {
        return {
          thread,
          metadata: {
            title: `Loan Account ${loanAccountNum}`,
            link: generateLoanAccountUrl({
              path: '/valley-bank/loan-accounts/summary',
              loanAccountId: loanAccountNum,
            }),
          },
        };
      });
    }
    return undefined;
  }, [threads]);

  // we dont want to return a skeleton here because it will mess with the height of the heading
  // if there are no threads to show, we should not show the drawer at all
  if (threadsWithMetadata === undefined) return null;

  return (
    <>
      <Tooltip label="Updates">
        <IconButton
          aria-label="Updates"
          key="Updates"
          icon={<Icon icon="bell" size="16px" />}
          bgColor={isOpen ? '#012E32' : colors.aurora['50']}
          color={isOpen ? 'white' : colors.black['95']}
          _hover={{ bgColor: '#2D6469', color: 'white' }}
          _focus={{ boxShadow: 'none' }}
          onClick={onOpen}
        />
      </Tooltip>
      <OverviewUpdatesDrawer
        isOpen={isOpen}
        onClose={onClose}
        threadsWithMetadata={threadsWithMetadata}
        refetchThreads={refetchThreads}
        externalOrgOptions={[]}
        onCreateNewCommentOnExistingThread={async (_newCommentId: string) => {
          // @valley if we want to send emails for comments, add it here

          refetchThreads();
        }}
      />
    </>
  );
};
